/**
 * CRUD Actions Utility
 *
 * Declarative delete and bulk action handling via data attributes.
 * Replaces inline JS on index/edit pages with a single auto-initializing module.
 *
 * Delete buttons:
 *   <button data-delete-url="/modifiers/123/delete"
 *           data-delete-name="Taco Toppings"
 *           data-delete-redirect="/modifiers">Delete</button>
 *
 * Bulk actions (on a container):
 *   <div data-bulk-url="/modifiers/bulk"
 *        data-bulk-resource="modifier group">
 *     <select data-bulk-select>...</select>
 *     <button data-bulk-apply>Apply</button>
 *     <input type="checkbox" data-bulk-item value="uuid" data-name="Item Name">
 *   </div>
 */

import { post } from './api.js';
import { ready, $$, $ } from './dom.js';

// ============================================================================
// Delete
// ============================================================================

function handleDelete(button) {
    const url = button.dataset.deleteUrl;
    const name = button.dataset.deleteName || '';
    const redirect = button.dataset.deleteRedirect;
    const method = button.dataset.deleteMethod || 'POST';

    const msg = name
        ? `Are you sure you want to delete "${name}"?\n\nThis action cannot be undone.`
        : 'Are you sure you want to delete this item?\n\nThis action cannot be undone.';

    if (!confirm(msg)) return;

    const originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = spinnerSvg();

    post(url, {})
        .then(data => {
            if (data.success) {
                if (redirect) {
                    window.location.href = redirect;
                } else {
                    // Remove the closest table row if on an index page
                    const row = button.closest('tr');
                    if (row) {
                        row.style.opacity = '0.5';
                        row.style.transition = 'opacity 0.3s ease';
                        setTimeout(() => {
                            row.remove();
                            // Reload if table is now empty
                            const tbody = row.closest('tbody') || document.querySelector('tbody');
                            if (tbody && tbody.children.length === 0) {
                                window.location.reload();
                            }
                        }, 300);
                    } else {
                        window.location.reload();
                    }
                }
            } else {
                throw new Error(data.message || 'Delete failed');
            }
        })
        .catch(error => {
            alert(error.message || 'An error occurred while deleting');
            button.disabled = false;
            button.innerHTML = originalHtml;
        });
}

// ============================================================================
// Bulk Actions
// ============================================================================

function initBulkContainer(container) {
    const bulkUrl = container.dataset.bulkUrl;
    const resource = container.dataset.bulkResource || 'item';
    const selectEl = $('[data-bulk-select]', container);
    const applyBtn = $('[data-bulk-apply]', container);
    const selectAllEl = $('[data-bulk-select-all]', container);

    if (!bulkUrl || !applyBtn) return;

    function getCheckboxes() {
        return $$('[data-bulk-item]', container);
    }

    function getChecked() {
        return getCheckboxes().filter(cb => cb.checked);
    }

    function updateState() {
        const checked = getChecked();
        const all = getCheckboxes();
        applyBtn.disabled = checked.length === 0;
        applyBtn.classList.toggle('opacity-50', checked.length === 0);
        applyBtn.classList.toggle('cursor-not-allowed', checked.length === 0);
        if (selectAllEl) {
            selectAllEl.checked = all.length > 0 && checked.length === all.length;
            selectAllEl.indeterminate = checked.length > 0 && checked.length < all.length;
        }
    }

    // Select all
    if (selectAllEl) {
        selectAllEl.addEventListener('change', function () {
            getCheckboxes().forEach(cb => cb.checked = this.checked);
            updateState();
        });
    }

    // Individual checkboxes
    container.addEventListener('change', e => {
        if (e.target.matches('[data-bulk-item]')) updateState();
    });

    // Apply
    applyBtn.addEventListener('click', function () {
        const action = selectEl ? selectEl.value : '';
        const checked = getChecked();

        if (!action) { alert('Please select an action'); return; }
        if (checked.length === 0) { alert(`Please select at least one ${resource}`); return; }

        const ids = checked.map(cb => cb.value);
        const names = checked.map(cb => cb.dataset.name || cb.value);

        const actionLabels = { delete: 'delete', activate: 'activate', deactivate: 'deactivate', available: 'mark as available', unavailable: 'mark as unavailable' };
        const actionText = actionLabels[action] || action;
        const plural = ids.length === 1 ? '' : 's';

        let msg = `Are you sure you want to ${actionText} ${ids.length} ${resource}${plural}?`;
        if (action === 'delete') {
            const nameList = names.slice(0, 5).join(', ');
            const more = names.length > 5 ? ` and ${names.length - 5} more` : '';
            msg += `\n\n${nameList}${more}\n\nThis action cannot be undone.`;
        }

        if (!confirm(msg)) return;

        const originalHtml = applyBtn.innerHTML;
        applyBtn.disabled = true;
        applyBtn.innerHTML = spinnerSvg() + ' Processing...';

        post(bulkUrl, { action, ids })
            .then(data => {
                if (data.success) {
                    setTimeout(() => window.location.reload(), 800);
                } else {
                    throw new Error(data.message || 'Bulk action failed');
                }
            })
            .catch(error => {
                alert(error.message || 'An error occurred');
                applyBtn.disabled = false;
                applyBtn.innerHTML = originalHtml;
            });
    });

    updateState();
}

// ============================================================================
// Duplicate
// ============================================================================

function handleDuplicate(button) {
    const url = button.dataset.duplicateUrl;
    const name = button.dataset.duplicateName || 'this item';

    if (!confirm(`Are you sure you want to duplicate "${name}"?`)) return;

    const originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = spinnerSvg();

    post(url, {})
        .then(data => {
            if (data.success) {
                if (data.redirect) {
                    setTimeout(() => { window.location.href = data.redirect; }, 800);
                } else {
                    setTimeout(() => window.location.reload(), 800);
                }
            } else {
                throw new Error(data.message || 'Duplicate failed');
            }
        })
        .catch(error => {
            alert(error.message || 'An error occurred while duplicating');
            button.disabled = false;
            button.innerHTML = originalHtml;
        });
}

// ============================================================================
// Helpers
// ============================================================================

function spinnerSvg() {
    return '<svg class="animate-spin h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';
}

// ============================================================================
// Auto-init
// ============================================================================

ready(() => {
    // Delete buttons
    document.addEventListener('click', e => {
        const btn = e.target.closest('[data-delete-url]');
        if (btn) { e.preventDefault(); handleDelete(btn); }

        const dupBtn = e.target.closest('[data-duplicate-url]');
        if (dupBtn) { e.preventDefault(); handleDuplicate(dupBtn); }
    });

    // Bulk action containers
    $$('[data-bulk-url]').forEach(initBulkContainer);
});

export { handleDelete, handleDuplicate, initBulkContainer };
